import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type OnboardingStage = 'checking' | 'invitation' | 'tour' | 'complete';
type TourStep = {
  path: string;
  target: string;
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  /** Analytics tab this step needs open before the spotlight can find its target */
  analyticsTab?: 'assessment' | 'progress';
};
const ONBOARDING_STORAGE_KEY = 'phoebe-onboarding-complete';
const SHOW_ONBOARDING_ON_EVERY_REFRESH = true;
const PAGE_TOUR_STEPS: Record<string, TourStep[]> = {
  '/admin': [{
    path: '/admin',
    target: '[data-phoebe-tour="company-info"]',
    eyebrow: 'Admin Hub · 1 of 5',
    title: 'Know your organization',
    description: 'This card shows your company, registered learners, and pending invitations at a glance.',
    action: 'Keep an eye on pending learners so no one is left waiting to get started.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="send-email"]',
    eyebrow: 'Admin Hub · 2 of 5',
    title: 'Invite pending learners',
    description: 'Use this tool to send an account-creation email to everyone still waiting to register.',
    action: 'Choose one learner or send a reminder to the whole pending group.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="dashboard"]',
    eyebrow: 'Admin Hub · 3 of 5',
    title: 'Read the performance snapshot',
    description: 'Review registration, active learning, and completion to see where attention is needed.',
    action: 'Start with the biggest gap, then choose one follow-up action.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="support"]',
    eyebrow: 'Admin Hub · 4 of 5',
    title: 'Get help when you need it',
    description: 'The support area is always here for questions about the platform or your learners.',
    action: 'Use Email Support when you need a hand from the team.'
  }, {
    path: '/admin',
    target: '[data-phoebe-tour="navigation-cards"]',
    eyebrow: 'Admin Hub · 5 of 5',
    title: 'Explore the rest of the workspace',
    description: 'These cards take you to Analytics, User Management, Reports, and AI-powered insights.',
    action: 'Next, let’s look at Analytics to see the story behind your learner data.'
  }],
  '/analytics': [{
    path: '/analytics',
    target: '[data-phoebe-tour="analytics"]',
    eyebrow: 'Analytics · 1 of 5',
    title: 'Find the learning story',
    description: 'Analytics brings together assessment performance, skill patterns, and learner progress so you can decide which learners need support.',
    action: 'Start with the organization-wide view, then narrow to the skills or learners that need attention.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-tabs"]',
    eyebrow: 'Analytics · 2 of 5',
    title: 'Choose the right view',
    description: 'Assessment Analytics helps you understand overall learning quality. Learner Progress shows individual attempt history, improvement, and completion by module.',
    action: 'Use the aggregate view to find a pattern, then switch to Learner Progress to understand who is affected.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-summary"]',
    eyebrow: 'Analytics · 3 of 5',
    title: 'Read the performance signals',
    description: 'Compare learners, pass rate, completion, score, mastery, attempts, and misinformation for a selected skill and time period.',
    action: 'High misinformation or falling pass rates usually mean learners need more practice time or better preparation before their next attempt.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-explorer"]',
    eyebrow: 'Analytics · 4 of 5',
    title: 'Explore the pattern behind the metric',
    description: 'Change the chart type, skill, or learner to inspect score distribution, performance by attempt, and answer confidence.',
    action: 'Use confidence data to spot learners who feel sure but are incorrect—an especially useful signal for targeted skill or pathway support.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="analytics-table"]',
    eyebrow: 'Analytics · 5 of 5',
    title: 'Turn findings into learner action',
    description: 'Use this table to compare learners, filter the group, and sort by score, improvement, attempts, or confidence signals.',
    action: 'Now let’s open Learner Progress to see how individual learners are doing.',
    analyticsTab: 'assessment'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-header"]',
    eyebrow: 'Learner Progress · 1 of 3',
    title: 'See every learner, module by module',
    description: 'The Learner Progress tab lists one row per learner and skill module, so you can see exactly where each person stands.',
    action: 'Filter to a single skill module when you want to focus on one area, then download the view to share it.',
    analyticsTab: 'progress'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-scores"]',
    eyebrow: 'Learner Progress · 2 of 3',
    title: 'Compare attempts and improvement',
    description: 'Attempts, first score, final score, and improvement together show whether a learner got there on their own or needed several tries.',
    action: 'Sort by attempts or first score to find learners who are struggling before they reach a passing final score.',
    analyticsTab: 'progress'
  }, {
    path: '/analytics',
    target: '[data-phoebe-tour="learner-progress-confidence"]',
    eyebrow: 'Learner Progress · 3 of 3',
    title: 'Check confidence alongside accuracy',
    description: 'The confident and correct rates show how sure learners were when they answered right on their first and final attempts.',
    action: 'A low confident-and-correct rate means the learner needs more practice before their next attempt—take them to User Management to assign it.',
    analyticsTab: 'progress'
  }],
  '/feedback': [{
    path: '/feedback',
    target: '[data-phoebe-tour="report-builder"]',
    eyebrow: 'Reports · 1 of 3',
    title: 'Build a report with Phoebe',
    description: 'Tap the sparkle button whenever you want help shaping a custom report.',
    action: 'Tell Phoebe who the report is for and what decision it needs to support.'
  }, {
    path: '/feedback',
    target: '[data-phoebe-tour="reports"]',
    eyebrow: 'Reports · 2 of 3',
    title: 'Start from a template',
    description: 'Choose learner progress, skills analytics, assessment results, or a completion summary.',
    action: 'Templates make it quick to generate a focused update.'
  }, {
    path: '/feedback',
    target: '[data-phoebe-tour="download-reports"]',
    eyebrow: 'Reports · 3 of 3',
    title: 'Find and download reports',
    description: 'Search across learners, skills, and existing reports before downloading.',
    action: 'Next, we’ll head to User Management—the best place to take your first action.'
  }],
  '/assessment': [{
    path: '/assessment',
    target: '[data-phoebe-tour="learners"]',
    eyebrow: 'User Management · 1 of 3',
    title: 'Add your learners',
    description: 'Start by adding one learner or importing a group from a CSV file.',
    action: 'Choose the option that matches how many people you need to bring into the platform.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="learner-list"]',
    eyebrow: 'User Management · 2 of 3',
    title: 'Find and select learners',
    description: 'Use search and filters to review your learner list, then select the people who need learning assigned.',
    action: 'For targeted training, start with learners who are unassigned, not started, or need a new pathway.'
  }, {
    path: '/assessment',
    target: '[data-phoebe-tour="skills-overview"]',
    eyebrow: 'User Management · 3 of 3',
    title: 'Review skills and pathways',
    description: 'Check available skill modules and pathways before assigning learning to your selected learners.',
    action: 'Use this overview to confirm capacity, then use the assignment bar to give learners the skills or pathway that will move them forward.'
  }],
  '/profile': [{
    path: '/profile',
    target: '[data-phoebe-tour="profile"]',
    eyebrow: 'Quick tour · Profile',
    title: 'Manage your profile',
    description: 'Update your personal details, security, display preferences, and notification settings here.',
    action: 'Use the left navigation to switch between General, Security, Preferences, and Settings.'
  }],
  '/alerts': [{
    path: '/alerts',
    target: '[data-phoebe-tour="alerts"]',
    eyebrow: 'Quick tour · Action Items',
    title: 'Prioritize what needs attention',
    description: 'Review learner alerts, complete follow-up actions, and keep the queue focused.',
    action: 'Start with urgent alerts, then mark resolved items complete.'
  }],
  '/insights-actions': [{
    path: '/insights-actions',
    target: '[data-phoebe-tour="insights"]',
    eyebrow: 'Quick tour · Insights',
    title: 'Turn patterns into action',
    description: 'Use AI-supported learning patterns to decide where support will matter most.',
    action: 'Prioritize high-impact insights, then plan one measurable intervention.'
  }],
  '/bulk-upload-results': [{
    path: '/bulk-upload-results',
    target: '[data-phoebe-tour="bulk-upload"]',
    eyebrow: 'Quick tour · Upload Results',
    title: 'Finish a learner import',
    description: 'Review imported learners and assign their first learning pathways before you finish.',
    action: 'Resolve any validation issues, then confirm the learning assignments.'
  }]
};
const TOUR_STEPS: TourStep[] = [...PAGE_TOUR_STEPS['/admin'], ...PAGE_TOUR_STEPS['/analytics'], ...PAGE_TOUR_STEPS['/feedback'], ...PAGE_TOUR_STEPS['/assessment']];
export function PhoebeOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stage, setStage] = useState<OnboardingStage>('checking');
  const [stepIndex, setStepIndex] = useState(0);
  const [tourSteps, setTourSteps] = useState<TourStep[]>(TOUR_STEPS);
  const [isPageTour, setIsPageTour] = useState(false);
  const [spotlight, setSpotlight] = useState<DOMRect | null>(null);
  const step = tourSteps[stepIndex];
  const isAdminHub = location.pathname === '/admin';
  const isLastStep = stepIndex === tourSteps.length - 1;
  const nextStep = tourSteps[stepIndex + 1];
  const nextLabel = !isPageTour && nextStep && nextStep.path !== step.path ? {
    '/analytics': 'Go to Analytics',
    '/feedback': 'Go to Reports',
    '/assessment': 'Go to User Management'
  }[nextStep.path] || 'Next' : 'Next';
  useEffect(() => {
    const hasCompletedOnboarding = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    setStage(SHOW_ONBOARDING_ON_EVERY_REFRESH || !hasCompletedOnboarding ? 'invitation' : 'complete');
  }, []);
  useEffect(() => {
    const startPageTour = (event: Event) => {
      const path = (event as CustomEvent<string>).detail;
      const pageSteps = PAGE_TOUR_STEPS[path];
      if (!pageSteps) return;
      setTourSteps(pageSteps);
      setIsPageTour(true);
      setStepIndex(0);
      setStage('tour');
      navigate(pageSteps[0].path);
    };
    window.addEventListener('phoebe:page-tour', startPageTour);
    return () => window.removeEventListener('phoebe:page-tour', startPageTour);
  }, [navigate]);
  useEffect(() => {
    if (stage !== 'invitation') return;
    const timeoutId = window.setTimeout(() => setStage('complete'), 30_000);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, stage]);

  // Highlight the nav item (and analytics tab) the current step belongs to
  useEffect(() => {
    const clearHighlights = () => {
      document.querySelectorAll('.phoebe-nav-active').forEach(element => element.classList.remove('phoebe-nav-active'));
    };
    if (stage !== 'tour') {
      clearHighlights();
      return;
    }
    const navKeys = [step.path];
    if (step.analyticsTab) navKeys.push(`analytics-tab-${step.analyticsTab}`);
    const applyHighlights = () => {
      clearHighlights();
      navKeys.forEach(key => {
        document.querySelectorAll(`[data-phoebe-nav="${key}"]`).forEach(element => element.classList.add('phoebe-nav-active'));
      });
    };
    applyHighlights();
    // The nav may still be mounting right after a route change
    const timeoutId = window.setTimeout(applyHighlights, 200);
    return () => {
      window.clearTimeout(timeoutId);
      clearHighlights();
    };
  }, [location.pathname, stage, step.path, step.analyticsTab]);
  useEffect(() => {
    if (stage !== 'tour') return;
    if (step.analyticsTab) {
      window.dispatchEvent(new CustomEvent('phoebe:analytics-tab', {
        detail: step.analyticsTab
      }));
    }
    const timeouts: number[] = [];
    const updateSpotlight = (attempt = 0) => {
      const target = document.querySelector(step.target);
      if (!target) {
        setSpotlight(null);
        // The target may belong to a tab that is still rendering
        if (attempt < 5) {
          timeouts.push(window.setTimeout(() => updateSpotlight(attempt + 1), 120));
        }
        return;
      }
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      timeouts.push(window.setTimeout(() => setSpotlight(target.getBoundingClientRect()), 250));
    };
    const handleReposition = () => updateSpotlight();
    updateSpotlight();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      timeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [location.pathname, stage, step.target, step.analyticsTab]);
  const spotlightStyle = useMemo(() => {
    if (!spotlight) return undefined;
    const inset = 8;
    return {
      top: Math.max(inset, spotlight.top - inset),
      left: Math.max(inset, spotlight.left - inset),
      width: Math.min(window.innerWidth - inset * 2, spotlight.width + inset * 2),
      height: Math.min(window.innerHeight - inset * 2, spotlight.height + inset * 2)
    };
  }, [spotlight]);
  const completeOnboarding = () => {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setStage('complete');
  };
  const startTour = () => {
    setTourSteps(TOUR_STEPS);
    setIsPageTour(false);
    setStepIndex(0);
    setStage('tour');
    navigate(TOUR_STEPS[0].path);
  };
  const startInvitationTour = () => {
    if (isAdminHub) {
      startTour();
      return;
    }
    const pageSteps = PAGE_TOUR_STEPS[location.pathname];
    if (!pageSteps) return;
    setTourSteps(pageSteps);
    setIsPageTour(true);
    setStepIndex(0);
    setStage('tour');
  };
  const moveToStep = (nextIndex: number) => {
    setStepIndex(nextIndex);
    navigate(tourSteps[nextIndex].path);
  };
  const finishTour = () => {
    if (isPageTour) {
      setStage('complete');
      openPhoebeChat({
        prompt: `I just took a quick tour of ${step.eyebrow.replace('Quick tour · ', '')}. What should I do next?`,
        response: `**You’re all set**\n\nYou’ve seen the key area of this page. I can help you take the next step, answer a question, or explain a specific control.`,
        suggestedQuestions: ['Help me choose my next step', 'Explain this page in more detail']
      });
      return;
    }
    completeOnboarding();
    navigate('/assessment');
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('phoebe:assign-learning'));
    }, 180);
  };
  if (stage === 'checking' || stage === 'complete') return null;
  return <AnimatePresence data-id="element-1549" data-mp="8gk2y79mnjib">
      {stage === 'invitation' && <motion.section initial={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.2,
      ease: 'easeOut'
    }} className="fixed bottom-20 right-4 z-[60] w-[min(17rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg sm:bottom-24 sm:right-6" role="dialog" aria-labelledby="phoebe-welcome-title" data-id="element-1550" data-mp="8g0o4zf9xb8i">
          <button type="button" onClick={() => setStage('complete')} className="absolute -right-2.5 -top-2.5 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" aria-label={isAdminHub ? "Dismiss Phoebe's platform tour invitation" : "Dismiss Phoebe's page tour invitation"} data-id="element-1551" data-mp="e5am13x1f1ox">
            <X size={15} aria-hidden="true" data-id="element-1552" data-mp="11uv7btgnyb1d" />
          </button>
          <button type="button" onClick={startInvitationTour} className="w-full text-left focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-1553" data-mp="16tpnenn8xlhs">
            <p className="text-xs font-bold text-[#5E7079]" data-id="element-1554" data-mp="1fv5yyypsrtnl">Phoebe · AI assistant</p>
            <h2 id="phoebe-welcome-title" className="mt-1 text-sm leading-snug text-[#001823]" data-id="element-1555" data-mp="30cf5iiemop5g">
              {isAdminHub ? 'Hi, I’m Phoebe. Want a quick tour of the whole platform?' : 'Hi, I’m Phoebe. Want to learn more about this screen?'}
            </h2>
          </button>
        </motion.section>}

      {stage === 'tour' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="phoebe-tour-title" data-id="element-1556" data-mp="2me8g7qnqvxhe">
          {spotlightStyle ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="pointer-events-none fixed z-[71] rounded-xl border-2 border-[#30718D] bg-white/5 shadow-[0_0_0_9999px_rgba(3,7,18,0.45)]" style={spotlightStyle} data-id="element-1557" data-mp="3a88fbu0yq4jj" /> : <div className="absolute inset-0 bg-gray-950/45" data-id="element-1558" data-mp="1soot2rof086" />}
          <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="fixed bottom-4 left-4 right-4 z-[72] mx-auto w-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl sm:bottom-6 sm:left-auto sm:right-6 sm:w-[30rem]" data-id="element-1559" data-mp="36b1d2f3pnipn">
            <div className="flex items-start justify-between gap-4" data-id="element-1560" data-mp="3p45bedrvijql">
              <div className="flex items-center gap-2 text-[#30718D]" data-id="element-1561" data-mp="3u2gkune9as8a">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D9EEF2]" data-id="element-1562" data-mp="3tzklcumrfvhq">
                  <Sparkles size={16} aria-hidden="true" data-id="element-1563" data-mp="3k3r0mnyw0vov" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-[0.1em]" data-id="element-1564" data-mp="3k5u1k31kw98d">Phoebe’s tour</p>
              </div>
              <button type="button" onClick={completeOnboarding} className="text-sm font-semibold text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1565" data-mp="24pczw2wood3w">
                Skip tour
              </button>
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500" data-id="element-1566" data-mp="2b2qqymfc9mpi">{step.eyebrow}</p>
            <h2 id="phoebe-tour-title" className="mt-1 text-lg font-bold text-gray-900" data-id="element-1567" data-mp="264xp25l2jev1">{step.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1568" data-mp="1ich51phrtct3">{step.description}</p>
            <div className="mt-3 rounded-lg bg-[#30718D]/5 px-3 py-2 text-xs leading-relaxed text-[#265A70]" data-id="element-1569" data-mp="1dhbicogjnu3y">{step.action}</div>
            <div className="mt-5 flex items-center justify-between gap-3" data-id="element-1570" data-mp="1ndocfng049z8">
              <button type="button" onClick={() => moveToStep(stepIndex - 1)} disabled={stepIndex === 0} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1571" data-mp="1nblb479eeuf7">
                <ArrowLeft size={16} aria-hidden="true" data-id="element-1572" data-mp="2loewcsygtqim" /> Back
              </button>
              {isLastStep ? <button type="button" onClick={finishTour} className="inline-flex items-center gap-2 rounded-lg bg-[#30718D] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-1573" data-mp="2gx6jfmxxfn0p">
                  {isPageTour ? 'Got it' : 'Assign skills & pathways'} <ArrowRight size={16} aria-hidden="true" data-id="element-1574" data-mp="30lfm4qx3jbeg" />
                </button> : <button type="button" onClick={() => moveToStep(stepIndex + 1)} className="inline-flex items-center gap-2 rounded-lg bg-[#30718D] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-1575" data-mp="1a8hw48l6fk63">
                  {nextLabel} <ArrowRight size={16} aria-hidden="true" data-id="element-1576" data-mp="meg52r052s4t" />
                </button>}
            </div>
          </motion.section>
        </motion.div>}
    </AnimatePresence>;
}